import { numericalColorSets } from '../features/visual-analytics/helpers/colors';

const schemaActionOpenAsset = {
  type: 'object',
  title: 'openAsset',
  description:
    'A button that opens an asset that is related to the current entity by matching properties. For an action to open an asset by ID, use the openAssetById action type instead.',
  properties: {
    type: { type: 'string', const: 'openAsset' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    assetType: { type: 'string', description: 'Identifier of the asset type to be opened.' },
    assetProperty: {
      type: 'string',
      description:
        'The asset to be opened is identified by this property, which should match the entity property. Note: this asset property should be indexed.',
    },
    entityProperty: { type: 'string', description: 'The entity property that should match the asset property.' },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the openAsset button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the openAsset button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'assetType', 'assetProperty', 'entityProperty'],
};

const schemaActionOpenAssetById = {
  type: 'object',
  title: 'openAssetById',
  description:
    'A button that opens an asset by its ID. For an action to open an asset by matching properties, use the openAsset action type instead.',
  properties: {
    type: { type: 'string', const: 'openAssetById' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    entityProperty: { type: 'string', description: 'The entity property that contains the asset ID.' },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the openAssetById button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the openAssetById button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'entityProperty'],
};

const schemaActionOpenEntity = {
  type: 'object',
  title: 'openEntity',
  description: 'A button that opens an entity that is related to the current entity.',
  properties: {
    type: { type: 'string', const: 'openEntity' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    newEntityType: { type: 'string', description: 'Identifier of the entity type to be opened.' },
    newEntityProperty: {
      type: 'string',
      description:
        'The entity to be opened is identified by this property, which should match the current entity property.',
    },
    currentEntityProperty: {
      type: 'string',
      description: 'The current entity property that should match the new entity property.',
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the openEntity button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the openEntity button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'newEntityType', 'newEntityProperty', 'currentEntityProperty'],
};

const schemaActionOpenAssetEntity = {
  type: 'object',
  title: 'openAssetEntity',
  description: 'A button that opens an entity view on an Asset for a specific EntityType.',
  properties: {
    type: { type: 'string', const: 'openAssetEntity' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    entityType: { type: 'string', description: 'Identifier of the entity type to be opened.' },
    assetType: { type: 'string', description: 'Identifier of the asset type to be opened.' },
    assetProperty: {
      type: 'string',
      description:
        'The asset to be opened is identified by this property, which should match the entity property. Note: this asset property should be indexed.',
    },
    entityProperty: { type: 'string', description: 'The entity property that should match the asset property.' },
    propertyValue: {
      type: 'string',
      description:
        'The property Value that should match the asset property. This will not check the entity for values.',
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the openAssetEntity button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the openAssetEntity button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'entityType', 'assetType', 'assetProperty'],
};

const schemaActionOpenUrl = {
  type: 'object',
  title: 'openUrl',
  description: 'A button that opens an url based on properties of the entity in the view.',
  properties: {
    type: { type: 'string', const: 'openUrl' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    url: {
      type: 'string',
      description:
        "Url to be opened. This can be formatted as a template, entity properties, with entity property placeholders between curly brackets (e.g. 'https://www.ncbi.nlm.nih.gov/gene/{Symbol}').",
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the openUrl button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the openUrl button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'url'],
};

const schemaActionOpenPDF = {
  type: 'object',
  title: 'openPDF',
  description: 'A button that opens a PDF Viewer based on properties of the entity in the view.',
  properties: {
    type: { type: 'string', const: 'openPDF' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    url: {
      type: 'string',
      description:
        "Url of the PDF to be opened. This can be formatted as a template, entity properties, with entity property placeholders between curly brackets (e.g. 'https://www.ncbi.nlm.nih.gov/gene/{Symbol}').",
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the OpenPdf button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the OpenPdf button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'url'],
};

const schemaActionDownloadFile = {
  type: 'object',
  title: 'downloadFile',
  description: 'A button that downloads a file on properties of the entity in the view.',
  properties: {
    type: { type: 'string', const: 'downloadFile' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    url: {
      type: 'string',
      description:
        "Location of the File to be downloaded. This can be formatted as a template, entity properties, with entity property placeholders between curly brackets (e.g. 'https://www.ncbi.nlm.nih.gov/gene/{Symbol}').",
    },
    fileType: {
      type: 'string',
      description:
        'Type of the file to be downloaded. This can be used to set the file extension or the define an entity attachment',
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the download button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the download button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'url', 'fileType'],
};

const schemaActionDashboardAction = {
  type: 'object',
  title: 'dashboardAction',
  description: 'A button that executes an action defined in the dashboard template.',
  properties: {
    type: { type: 'string', const: 'dashboardAction' },
    name: { type: 'string', description: 'Name of the action.' },
    icon: { type: 'string', description: 'Icon to be displayed on the action button.' },
    actionId: {
      type: 'string',
      description: 'Identifier of the action to be executed as defined in the dashboard template.',
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable the dashboard action button',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable the dashboard action button',
      items: { type: 'string' },
    },
  },
  required: ['type', 'name', 'icon', 'actionId'],
};

const schemaActions = {
  type: 'object',
  title: 'actions',
  description: 'Defines a set of action buttons',
  properties: {
    type: { type: 'string', const: 'actions' },
    actions: {
      type: 'array',
      description: 'Enumerates the set of configured actions',
      items: {
        anyOf: [
          schemaActionOpenAsset,
          schemaActionOpenAssetById,
          schemaActionOpenEntity,
          schemaActionOpenUrl,
          schemaActionOpenPDF,
          schemaActionDownloadFile,
          schemaActionOpenAssetEntity,
          schemaActionDashboardAction,
        ],
      },
    },
  },
  required: ['type', 'actions'],
};

const schemaPropertyList = {
  type: 'object',
  title: 'propertyList',
  description: 'Displays a set of entity properties',
  properties: {
    type: { type: 'string', const: 'propertyList' },
    title: { type: 'string', description: 'The title of the section.' },
    dataTableId: {
      type: 'string',
      description: 'Identifier of the data table in the data context to read properties from.',
    },
    propertyGroupIds: {
      type: 'array',
      description: 'Set of property groups to display.',
      items: { type: 'string' },
    },
  },
  required: ['type', 'title'],
};

const schemaNetworkLinks = {
  type: 'object',
  title: 'network_links',
  description: 'Displays a table with information from links to the displayed entity in a network',
  properties: {
    type: { type: 'string', const: 'network_links' },
    title: { type: 'string', description: 'The title of the section.' },
    nodesTableId: {
      type: 'string',
      description: 'Identifier of the data table in the data context containing the nodes of the network.',
    },
    edgesTableId: {
      type: 'string',
      description: 'Identifier of the data table in the data context containing the edges of the network.',
    },
    columnsNodes: {
      type: 'array',
      description: 'Set of properties to display for the related nodes (in the table identified by nodesTableId).',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Identifier of the property.' },
          width: { type: 'number', description: 'Width of the displayed column.' },
        },
        required: ['id'],
      },
    },
    columnsEdges: {
      type: 'array',
      description: 'Set of properties to display for the related edges (in the table identified by edgesTableId).',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Identifier of the property.' },
          width: { type: 'number', description: 'Width of the displayed column.' },
        },
        required: ['id'],
      },
    },
  },
  required: ['type', 'title', 'nodesTableId', 'edgesTableId', 'columnsNodes', 'columnsEdges'],
};

const schemaRelatedTable = {
  type: 'object',
  title: 'relatedTable',
  description: 'Displays a table with entities that are related to the active entity in the view',
  properties: {
    type: { type: 'string', const: 'relatedTable' },
    title: { type: 'string', description: 'The title of the section.' },
    tableFrom: {
      type: 'string',
      description: 'Identifier of the data table containing the active entity to be matched.',
    },
    linkFrom: {
      type: 'string',
      description: 'Identifier of the property of the active entity to be matched (in tableFrom).',
    },
    tableTo: {
      type: 'string',
      description: 'Identifier of the data table containing the related entities to be displated.',
    },
    linkTo: {
      type: 'string',
      description:
        'Identifier of the property of the related entities (in tableTo) to be matched. Matching criterion is tableTo.linkTo == tableFrom.linkFrom, where the tableFrom row corresponds to the entity in the current entity view.',
    },
    sortBy: {
      type: 'string',
      description: 'Column to use for default sorting of the table rows.',
    },
    sortDir: {
      type: 'string',
      description: 'Default sort direction (asc/desc).',
    },
    columnsTo: {
      type: 'array',
      description: 'Set of properties to display for the related table (tableTo).',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Identifier of the property.' },
          width: { type: 'number', description: 'Width of the displayed column.' },
        },
        required: ['id'],
      },
    },
    openSectionOnSelectRelated: {
      type: 'string',
      description: 'Identifier of the dashboard section to open when the "Select all related" button is clicked.',
    },
  },
  required: ['type', 'title', 'tableFrom', 'linkFrom', 'tableTo', 'linkTo', 'columnsTo'],
};

const schemaPropertyNumericalTiles = {
  type: 'object',
  title: 'propertyNumericalTiles',
  description: 'Displays a set of numerical properties as color coded tiles',
  properties: {
    type: { type: 'string', const: 'propertyNumericalTiles' },
    title: { type: 'string', description: 'The title of the section.' },
    dataTableId: {
      type: 'string',
      description: 'Identifier of the data table in the data context to read properties from.',
    },
    propertyGroupId: { type: 'string', description: 'Identifier of the property group to display.' },
    tileWidth: { type: 'number', description: 'Width in pixels for a single tile.' },
    maxLineCount: { type: 'number', description: 'Maximum number of text lines in a single tile.' },
    minVal: { type: 'number', description: 'Minimum value to clip the range.' },
    maxVal: { type: 'number', description: 'Maximum value to clip the range.' },
    colorScheme: {
      type: 'string',
      description: 'Color schema to be used.',
      enum: numericalColorSets.map((colorSet) => colorSet.name),
    },
    sortBy: {
      type: 'string',
      description: 'Sorting criteria for the tiles.',
      enum: ['none', 'valInc', 'valDec'],
    },
  },
  required: ['type', 'title', 'dataTableId', 'propertyGroupId', 'minVal', 'maxVal'],
};

const schemaExternalData = {
  type: 'object',
  title: 'externalData',
  description: 'Displays external data sources linked to the active entity in the view',
  properties: {
    type: { type: 'string', const: 'externalData' },
    sectionId: { type: 'string', description: 'Unique identifier of the section.' },
    title: { type: 'string', description: 'The title of the section.' },
    description: { type: 'string', description: 'Optional description text for the section. Can include markdown.' },
    resourceType: {
      type: 'string',
      description: 'Type of external resource to be opened.',
      enum: ['img'],
    },
    isGallery: { type: 'boolean', description: 'If true, multiple resources are shown in a gallery view.' },
    height: { type: 'number', description: 'Height in pixels of the embedded viewer.' },
    url: {
      type: 'string',
      description:
        "Url to be opened. This can be formatted as a template, entity properties, with entity property placeholders between curly brackets (e.g. 'https://www.ncbi.nlm.nih.gov/gene/{Symbol}').",
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable this section.',
    },
  },
  required: ['type', 'title', 'sectionId', 'resourceType', 'url'],
};

const schemaImage = {
  type: 'object',
  title: 'image',
  description: 'Displays an image linked to the active entity in the view.',
  properties: {
    type: { type: 'string', const: 'image' },
    sectionId: { type: 'string', description: 'Unique identifier of the section.' },
    title: { type: 'string', description: 'The title of the section.' },
    description: { type: 'string', description: 'Optional description text for the section. Can include markdown.' },
    height: { type: 'number', description: 'Height in pixels of the embedded viewer. Auto by default.' },
    url: {
      type: 'string',
      description:
        "Url to be opened. This can be formatted as a template, entity properties, with entity property placeholders between curly brackets (e.g. 'https://www.ncbi.nlm.nih.gov/gene/{Symbol}').",
    },
    fileType: {
      type: 'string',
      description:
        'Type of the image to be displayed. Set to "attachment" for entity attachments, else set to the image file type (e.g. "png", "jpg").',
    },
    requiredProperty: {
      type: 'string',
      description: 'The entity property that should be defined to enable this section.',
    },
    requiredPropertyValues: {
      type: 'array',
      description: 'The entity property values that should be defined to enable this section.',
      items: { type: 'string' },
    },
  },
  required: ['type', 'title', 'sectionId', 'fileType', 'url'],
};

const schemaEntityView = {
  type: 'object',
  properties: {
    assetType: { type: 'string', description: 'Asset type this template defines entity views for.' },
    templateId: { type: 'string', description: 'Unique identifier of the template.' },
    version: { type: 'string', description: 'Template version.' },
    displayName: { type: 'string', description: 'Display name of this template.' },
    entities: {
      type: 'array',
      description: 'The list of templates for all relevant entity types.',
      items: {
        type: 'object',
        description: 'Defines the view for an individual entity type.',
        properties: {
          entityType: { type: 'string', description: 'Identifier of the entity type.' },
          displayName: { type: 'string', description: 'Display name in the view.' },
          titleProperty: {
            type: 'string',
            description:
              'The entity property to be used as title in the single entity view. If not defined, the primary key of the entity will be used as title.',
          },
          elements: {
            type: 'array',
            description:
              'List of all elements that appear in the entity view. These elements are displayed sequentially.',
            items: {
              anyOf: [
                schemaActions,
                schemaExternalData,
                schemaImage,
                schemaPropertyList,
                schemaNetworkLinks,
                schemaRelatedTable,
                schemaPropertyNumericalTiles,
              ],
            },
          },
        },
        required: ['entityType', 'elements'],
      },
    },
  },
  required: ['entities'],
};

export default schemaEntityView;
